import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TreeList, {
    Column as TLColumn,
    Selection as TLSelection,
    Scrolling as TLScrolling,
    RowDragging,
} from 'devextreme-react/tree-list';
import DataGrid, {
    Column,
    Editing,
    Scrolling,
    GroupPanel,
    Grouping,
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import Form, { SimpleItem } from 'devextreme-react/form';
import notify from 'devextreme/ui/notify';
import competitionEntityApi from '../../../api/competitionEntityApi';
import { STROKE_LOOKUP, GENDER_LOOKUP, ROUND_LOOKUP } from '../../../shared/constants';
import { SplitPanel } from '../../utils/split-panel/SplitPanel';
import classNames from 'classnames';

// ── Module-level helpers ───────────────────────────────────────────────────

const sortByIndex = (a, b) => {
    const aS = (a.sortIndex > 0 ? a.sortIndex : null) ?? (a.number || 0) * 10000;
    const bS = (b.sortIndex > 0 ? b.sortIndex : null) ?? (b.number || 0) * 10000;
    return aS - bS;
};

// ── Component ──────────────────────────────────────────────────────────────

const StructureLeftPane = React.memo(React.forwardRef(function StructureLeftPane({ treeItems, expandedKeys, onExpandedRowKeysChange, onSelect, onDragChange, onReorder, canEdit }, ref) {
    const treeRef = useRef(null);
    React.useImperativeHandle(ref, () => ({
        clearSelection: () => treeRef.current?.instance?.clearSelection(),
    }), []);
    return (
        <div className='dx-card' style={{ padding: 12, display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
            <div style={{ fontWeight: 600, marginBottom: 8, flexShrink: 0 }}>
                Baumstruktur
                {canEdit && (
                    <span style={{ fontSize: 11, color: '#888', marginLeft: 8, fontWeight: 400 }}>
                        Drag &amp; Drop zum Sortieren
                    </span>
                )}
            </div>
            <TreeList
                ref={treeRef}
                dataSource={treeItems}
                keyExpr='id'
                parentIdExpr='parentId'
                showBorders={false}
                rootValue={null}
                columnAutoWidth
                hoverStateEnabled
                expandedRowKeys={expandedKeys}
                onExpandedRowKeysChange={onExpandedRowKeysChange}
                onSelectionChanged={(e) => { const node = e.selectedRowsData?.[0]; onSelect(node || null); }}
                style={{ flex: 1, minHeight: 0 }}
            >
                {canEdit && (
                    <RowDragging
                        allowReordering
                        onDragChange={onDragChange}
                        onReorder={onReorder}
                        showDragIcons
                    />
                )}
                <TLSelection mode='single' />
                <TLScrolling mode='virtual' />
                <TLColumn dataField='title' caption='Element' />
                <TLColumn dataField='number' caption='Nr.' width={55} />
            </TreeList>
        </div>
    );
}));

export function CompetitionStructureTab({
    competitionId,
    sessions,
    events,
    heats,
    rankings,
    rankinggroups,
    results,
    athletes,
    accessToken,
    onEntityChange,
}) {
    const [selectedType, setSelectedType] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [formData, setFormData] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isReordering, setIsReordering] = useState(false);
    const [isSavingRanking, setIsSavingRanking] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState(() =>
        (sessions || []).map(s => `session-${s.sessionId}`)
    );

    // Refs to always-current data — avoid stale closures in DnD callbacks
    const sessionsRef = useRef(sessions);
    const eventsRef = useRef(events);
    const heatsRef = useRef(heats);
    const treeItemsRef = useRef([]);
    useEffect(() => { sessionsRef.current = sessions; }, [sessions]);
    useEffect(() => { eventsRef.current = events; }, [events]);
    useEffect(() => { heatsRef.current = heats; }, [heats]);
    // Auto-expand newly added sessions
    useEffect(() => {
        const sessionKeys = (sessions || []).map(s => `session-${s.sessionId}`);
        setExpandedKeys(prev => {
            const newKeys = sessionKeys.filter(k => !prev.includes(k));
            return newKeys.length ? [...prev, ...newKeys] : prev;
        });
    }, [sessions]);

    const canEdit = !!accessToken;

    // ── Reset form when selection changes ──────────────────────────────────
    useEffect(() => {
        if (!selectedType || !selectedId) { setFormData(null); setIsDirty(false); return; }
        let data = null;
        if (selectedType === 'session') data = sessionsRef.current?.find(x => x.sessionId === selectedId);
        else if (selectedType === 'event') data = eventsRef.current?.find(x => x.eventId === selectedId);
        else if (selectedType === 'heat') data = heatsRef.current?.find(x => x.heatId === selectedId);
        setFormData(data ? { ...data } : null);
        setIsDirty(false);
        setIsEditing(false);
    }, [selectedType, selectedId]); // intentionally excludes sessions/events/heats

    // ── Tree data ──────────────────────────────────────────────────────────
    const treeItems = useMemo(() => {
        const sessionNodes = [...(sessions || [])].sort(sortByIndex).map(s => ({
            id: `session-${s.sessionId}`,
            parentId: null,
            type: 'session',
            refId: s.sessionId,
            number: s.number,
            sortIndex: s.sortIndex,
            title: `Abschnitt ${s.number || ''}${s.description ? ' · ' + s.description : ''}`.trim(),
        }));

        const eventNodes = [...(events || [])].sort(sortByIndex).map(ev => ({
            id: `event-${ev.eventId}`,
            parentId: ev.sessionId ? `session-${ev.sessionId}` : null,
            type: 'event',
            refId: ev.eventId,
            number: ev.number,
            sortIndex: ev.sortIndex,
            title: `WK ${ev.number || ''}${ev.name ? ' · ' + ev.name : ''}`,
        }));

        const heatNodes = [...(heats || [])].sort(sortByIndex).map(h => ({
            id: `heat-${h.heatId}`,
            parentId: h.eventId ? `event-${h.eventId}` : null,
            type: 'heat',
            refId: h.heatId,
            number: h.number,
            sortIndex: h.sortIndex,
            title: `Lauf ${h.number || ''}`,
        }));

        const items = [...sessionNodes, ...eventNodes, ...heatNodes];
        treeItemsRef.current = items;
        return items;
    }, [sessions, events, heats]);

    const selectedNodeKey = useMemo(
        () => (selectedType && selectedId ? [`${selectedType}-${selectedId}`] : []),
        [selectedType, selectedId]
    );

    // ── Lookup tables ──────────────────────────────────────────────────────
    const sessionLookup = useMemo(
        () => (sessions || []).map(s => ({ id: s.sessionId, name: s.description || `Abschnitt ${s.number}` })),
        [sessions]
    );

    const eventLookup = useMemo(
        () => (events || []).map(ev => ({ id: ev.eventId, name: `WK ${ev.number} – ${ev.name || `${ev.distance}m`}` })),
        [events]
    );

    const eventRankingGroupIds = useMemo(() => {
        if (selectedType !== 'event' || !selectedId) return new Set();
        return new Set((rankinggroups || []).filter(rg => rg.eventId === selectedId).map(rg => rg.rankinggroupId));
    }, [selectedType, selectedId, rankinggroups]);

    const eventRankings = useMemo(
        () => (selectedType !== 'event' ? [] : (rankings || []).filter(r => eventRankingGroupIds.has(r.rankinggroupId))),
        [selectedType, rankings, eventRankingGroupIds]
    );

    const rankinggroupLookup = useMemo(
        () => (rankinggroups || [])
            .filter(rg => rg.eventId === selectedId)
            .map(rg => ({ id: rg.rankinggroupId, name: rg.title || rg.detail || 'Unbenannt' })),
        [rankinggroups, selectedId]
    );

    const resultLookup = useMemo(() => {
        if (!results || !athletes) return [];
        const am = athletes.reduce((m, a) => { m[a.athleteId] = `${a.firstname || ''} ${a.name || ''}`.trim(); return m; }, {});
        return results.map(r => ({ id: r.resultId, name: am[r.athleteId] || `Ergebnis ${r.resultId}` }));
    }, [results, athletes]);

    const handleTreeSelect = useCallback((node) => {
        if (!node) { setSelectedType(null); setSelectedId(null); return; }
        setSelectedType(node.type);
        setSelectedId(node.refId);
    }, []);
    const leftPaneRef = useRef(null);

    // ── Form field update ──────────────────────────────────────────────────
    const updateFormField = useCallback((field, value) => {
        setFormData(prev => prev ? { ...prev, [field]: value } : prev);
        setIsDirty(true);
    }, []);

    // ── Save ───────────────────────────────────────────────────────────────
    const handleSave = useCallback(async () => {
        if (!formData || !accessToken || !selectedType || !selectedId) return;
        setIsSaving(true);
        try {
            if (selectedType === 'session') {
                const current = sessionsRef.current?.find(s => s.sessionId === selectedId) || {};
                const saved = await competitionEntityApi.sessions.update(competitionId, selectedId, { ...current, ...formData, sessionId: selectedId, competitionId }, accessToken);
                onEntityChange?.('Session', 'update', saved);
            } else if (selectedType === 'event') {
                const current = eventsRef.current?.find(ev => ev.eventId === selectedId) || {};
                const saved = await competitionEntityApi.events.update(competitionId, selectedId, { ...current, ...formData, eventId: selectedId, competitionId }, accessToken);
                onEntityChange?.('Event', 'update', saved);
            } else if (selectedType === 'heat') {
                const current = heatsRef.current?.find(h => h.heatId === selectedId) || {};
                const saved = await competitionEntityApi.heats.update(competitionId, selectedId, { ...current, ...formData, heatId: selectedId, competitionId }, accessToken);
                onEntityChange?.('Heat', 'update', saved);
            }
            setIsDirty(false);
            setIsEditing(false);
            notify('Gespeichert', 'success', 2000);
        } catch (err) {
            notify(err?.response?.data || err?.message || 'Fehler beim Speichern', 'error', 4000);
        } finally {
            setIsSaving(false);
        }
    }, [accessToken, competitionId, formData, onEntityChange, selectedId, selectedType]);

    // ── Cancel ─────────────────────────────────────────────────────────────
    const handleCancel = useCallback(() => {
        if (!selectedType || !selectedId) return;
        let data = null;
        if (selectedType === 'session') data = sessionsRef.current?.find(x => x.sessionId === selectedId);
        else if (selectedType === 'event') data = eventsRef.current?.find(x => x.eventId === selectedId);
        else if (selectedType === 'heat') data = heatsRef.current?.find(x => x.heatId === selectedId);
        setFormData(data ? { ...data } : null);
        setIsDirty(false);
        setIsEditing(false);
    }, [selectedType, selectedId]);

    // ── Delete ─────────────────────────────────────────────────────────────
    const handleDelete = useCallback(async () => {
        if (!accessToken || !selectedType || !selectedId) return;
        if (!window.confirm('Element wirklich löschen?')) return;
        setIsSaving(true);
        try {
            if (selectedType === 'session') {
                await competitionEntityApi.sessions.remove(competitionId, selectedId, accessToken);
                onEntityChange?.('Session', 'delete', { sessionId: selectedId });
            } else if (selectedType === 'event') {
                await competitionEntityApi.events.remove(competitionId, selectedId, accessToken);
                onEntityChange?.('Event', 'delete', { eventId: selectedId });
            } else if (selectedType === 'heat') {
                await competitionEntityApi.heats.remove(competitionId, selectedId, accessToken);
                onEntityChange?.('Heat', 'delete', { heatId: selectedId });
            }
            setSelectedType(null);
            setSelectedId(null);
            leftPaneRef.current?.clearSelection();
            notify('Gelöscht', 'success', 2000);
        } catch (err) {
            notify(err?.response?.data || err?.message || 'Fehler beim Löschen', 'error', 4000);
        } finally {
            setIsSaving(false);
        }
    }, [accessToken, competitionId, onEntityChange, selectedId, selectedType]);

    // ── Drag & Drop – validate drop target ────────────────────────────────
    const handleDragChange = useCallback((e) => {
        const visibleRows = e.component.getVisibleRows();
        const targetData = visibleRows[e.toIndex]?.data;
        if (!targetData) { e.cancel = true; return; }
        if (e.itemData.type !== targetData.type || e.itemData.parentId !== targetData.parentId) {
            e.cancel = true;
        }
    }, []);

    // ── Drag & Drop – apply new order ──────────────────────────────────────
    const handleReorder = useCallback(async (e) => {
        if (!canEdit) return;
        const sourceData = e.itemData;

        // get current sibling list (sorted as displayed) from the live ref
        const currentSiblings = treeItemsRef.current.filter(
            item => item.type === sourceData.type && item.parentId === sourceData.parentId
        );

        // target item at visual drop position (before the actual data change)
        const visibleRows = e.component.getVisibleRows();
        const targetData = visibleRows[e.toIndex]?.data;
        if (!targetData) return;

        const fromIdx = currentSiblings.findIndex(s => s.id === sourceData.id);
        const toIdx = currentSiblings.findIndex(s => s.id === targetData.id);
        if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

        // build new order
        const newOrder = [...currentSiblings];
        const [moved] = newOrder.splice(fromIdx, 1);
        newOrder.splice(toIdx, 0, moved);

        // assign new sortIndex values (10, 20, 30, …)
        const newSortIndices = new Map(newOrder.map((item, idx) => [item.refId, (idx + 1) * 10]));

        setIsReordering(true);
        try {
            if (sourceData.type === 'session') {
                const updated = (sessionsRef.current || [])
                    .filter(s => newSortIndices.has(s.sessionId))
                    .map(s => ({ ...s, sortIndex: newSortIndices.get(s.sessionId) }));
                updated.forEach(s => onEntityChange?.('Session', 'update', s));
                await competitionEntityApi.sessions.reorder(
                    competitionId,
                    updated.map(s => ({ sessionId: s.sessionId, sortIndex: s.sortIndex })),
                    accessToken
                );
            } else if (sourceData.type === 'event') {
                const updated = (eventsRef.current || [])
                    .filter(ev => newSortIndices.has(ev.eventId))
                    .map(ev => ({ ...ev, sortIndex: newSortIndices.get(ev.eventId) }));
                updated.forEach(ev => onEntityChange?.('Event', 'update', ev));
                await competitionEntityApi.events.reorder(
                    competitionId,
                    updated.map(ev => ({ eventId: ev.eventId, sortIndex: ev.sortIndex })),
                    accessToken
                );
            } else if (sourceData.type === 'heat') {
                const updated = (heatsRef.current || [])
                    .filter(h => newSortIndices.has(h.heatId))
                    .map(h => ({ ...h, sortIndex: newSortIndices.get(h.heatId) }));
                updated.forEach(h => onEntityChange?.('Heat', 'update', h));
                await competitionEntityApi.heats.reorder(
                    competitionId,
                    updated.map(h => ({ heatId: h.heatId, sortIndex: h.sortIndex })),
                    accessToken
                );
            }
            notify('Reihenfolge gespeichert', 'success', 1500);
        } catch (err) {
            notify(err?.response?.data || err?.message || 'Fehler beim Speichern der Reihenfolge', 'error', 4000);
        } finally {
            setIsReordering(false);
        }
    }, [canEdit, competitionId, accessToken, onEntityChange]);

    // ── Rankings save handler ──────────────────────────────────────────────
    const handleRankingSaving = useCallback(async (e) => {
        if (!e.changes?.length) return;
        if (!accessToken) { notify('Kein Zugriffstoken', 'warning', 3000); e.cancel = true; return; }
        e.cancel = true;
        setIsSavingRanking(true);
        try {
            for (const change of e.changes) {
                if (change.type === 'insert') {
                    const saved = await competitionEntityApi.rankings.insert(competitionId, { ...change.data, competitionId }, accessToken);
                    onEntityChange?.('Ranking', 'insert', saved);
                }
                if (change.type === 'update') {
                    const current = (rankings || []).find(r => r.rankingId === change.key) || {};
                    const saved = await competitionEntityApi.rankings.update(competitionId, change.key, { ...current, ...change.data, rankingId: change.key, competitionId }, accessToken);
                    onEntityChange?.('Ranking', 'update', saved);
                }
                if (change.type === 'remove') {
                    await competitionEntityApi.rankings.remove(competitionId, change.key, accessToken);
                    onEntityChange?.('Ranking', 'delete', { rankingId: change.key });
                }
            }
            e.component.cancelEditData();
        } catch (err) {
            notify(err?.response?.data || err?.message || 'Fehler beim Speichern', 'error', 4000);
        } finally {
            setIsSavingRanking(false);
        }
    }, [accessToken, competitionId, onEntityChange, rankings]);

    // ── Form toolbar ───────────────────────────────────────────────────────
    const renderFormToolbar = useCallback((title) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
            {canEdit && (
                <div style={{ display: 'flex', gap: 6 }}>
                    {isEditing ? (
                        <>
                            <Button text='Speichern' type='default' stylingMode='contained'
                                disabled={isSaving} onClick={handleSave} />
                            <Button text='Abbrechen' stylingMode='outlined'
                                disabled={isSaving} onClick={handleCancel} />
                            <Button icon='trash' stylingMode='outlined' hint='Löschen'
                                disabled={isSaving} onClick={handleDelete} />
                        </>
                    ) : (
                        <>
                            <Button text='Bearbeiten' icon='edit' stylingMode='outlined'
                                disabled={!formData} onClick={() => setIsEditing(true)} />
                            <Button icon='trash' stylingMode='outlined' hint='Löschen'
                                disabled={!formData || isSaving} onClick={handleDelete} />
                        </>
                    )}
                </div>
            )}
        </div>
    ), [canEdit, formData, handleCancel, handleDelete, handleSave, isEditing, isSaving]);

    // ── Session form ───────────────────────────────────────────────────────
    const renderSessionForm = () => {
        if (!formData) return <div style={{ color: '#888', padding: 16 }}>Kein Abschnitt gefunden.</div>;
        const f = formData;
        return (
            <div className={classNames('dx-card', 'plain-styled-form', { 'view-mode': !isEditing })} style={{ padding: 16, overflow: 'auto' }}>
                {renderFormToolbar(`Abschnitt ${f.number || ''} – ${f.description || ''}`)}
                <Form formData={f} labelMode='floating' colCount={4} readOnly={!isEditing} onFieldDataChanged={(e) => updateFormField(e.dataField, e.value)}>
                    <SimpleItem dataField='number' label={{ text: 'Nr.' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='description' label={{ text: 'Bezeichnung' }} editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='date' label={{ text: 'Datum' }} editorType='dxDateBox' editorOptions={{ type: 'date', displayFormat: 'dd.MM.yyyy', stylingMode: 'filled' }} />
                    <SimpleItem dataField='entrytime' label={{ text: 'Einlass' }} editorType='dxDateBox' editorOptions={{ type: 'time', displayFormat: 'HH:mm', stylingMode: 'filled' }} />
                    <SimpleItem dataField='warmup' label={{ text: 'Einschwimmen' }} editorType='dxDateBox' editorOptions={{ type: 'time', displayFormat: 'HH:mm', stylingMode: 'filled' }} />
                    <SimpleItem dataField='officalmeeting' label={{ text: 'KR-Besprechung' }} editorType='dxDateBox' editorOptions={{ type: 'time', displayFormat: 'HH:mm', stylingMode: 'filled' }} />
                    <SimpleItem dataField='start' label={{ text: 'Start' }} editorType='dxDateBox' editorOptions={{ type: 'time', displayFormat: 'HH:mm', stylingMode: 'filled' }} />
                    <SimpleItem dataField='endSession' label={{ text: 'Ende' }} editorType='dxDateBox' editorOptions={{ type: 'time', displayFormat: 'HH:mm', stylingMode: 'filled' }} />
                    <SimpleItem dataField='endProtocol' label={{ text: 'Ende Protokoll' }} editorType='dxDateBox' editorOptions={{ type: 'time', displayFormat: 'HH:mm', stylingMode: 'filled' }} />
                    <SimpleItem dataField='absoluteTimes' label={{ text: 'Abs. Zeiten' }} editorType='dxCheckBox' />
                    <SimpleItem dataField='breakHeats' label={{ text: 'Pause Läufe' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='breakEvents' label={{ text: 'Pause WK' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='breakHeatsBackstroke' label={{ text: 'Pause Läufe Rücken' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='breakEventsBackstroke' label={{ text: 'Pause WK Rücken' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='breakHeatsRelay' label={{ text: 'Pause Läufe Staffel' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='breakEventsRelay' label={{ text: 'Pause WK Staffel' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='comment' label={{ text: 'Kommentar' }} editorType='dxTextArea' editorOptions={{ stylingMode: 'filled', height: 60 }} />
                </Form>
            </div>
        );
    };

    // ── Event form ─────────────────────────────────────────────────────────
    const renderEventForm = () => {
        if (!formData) return <div style={{ color: '#888', padding: 16 }}>Kein Wettkampf gefunden.</div>;
        const f = formData;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className={classNames('dx-card', 'plain-styled-form', { 'view-mode': !isEditing })} style={{ padding: 16, overflow: 'auto', flexShrink: 0 }}>
                    {renderFormToolbar(`WK ${f.number || ''} – ${f.name || ''}`)}
                    <Form formData={f} labelMode='floating' colCount={4} readOnly={!isEditing} onFieldDataChanged={(e) => updateFormField(e.dataField, e.value)}>
                        <SimpleItem dataField='number' label={{ text: 'Nr.' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='name' label={{ text: 'Name' }} editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='sessionId' label={{ text: 'Abschnitt' }} editorType='dxSelectBox' editorOptions={{ dataSource: sessionLookup, valueExpr: 'id', displayExpr: 'name', stylingMode: 'filled' }} />
                        <SimpleItem dataField='distance' label={{ text: 'Strecke (m)' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='stroke' label={{ text: 'Lage' }} editorType='dxSelectBox' editorOptions={{ dataSource: STROKE_LOOKUP, valueExpr: 'id', displayExpr: 'name', stylingMode: 'filled' }} />
                        <SimpleItem dataField='gender' label={{ text: 'Geschlecht' }} editorType='dxSelectBox' editorOptions={{ dataSource: GENDER_LOOKUP, valueExpr: 'id', displayExpr: 'name', stylingMode: 'filled' }} />
                        <SimpleItem dataField='round' label={{ text: 'Runde' }} editorType='dxSelectBox' editorOptions={{ dataSource: ROUND_LOOKUP, valueExpr: 'id', displayExpr: 'name', stylingMode: 'filled' }} />
                        <SimpleItem dataField='oldestYob' label={{ text: 'Jg. älteste' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='youngestYob' label={{ text: 'Jg. jüngste' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                        <SimpleItem dataField='isRelay' label={{ text: 'Staffel' }} editorType='dxCheckBox' />
                        <SimpleItem dataField='relayAthleteCount' label={{ text: 'Staffelgröße' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} visible={f.isRelay} />
                        <SimpleItem dataField='estimatedStarttime' label={{ text: 'Gesch. Start' }} editorType='dxDateBox' editorOptions={{ type: 'time', displayFormat: 'HH:mm', stylingMode: 'filled' }} />
                        <SimpleItem dataField='isMasters' label={{ text: 'Masters' }} editorType='dxCheckBox' />
                        <SimpleItem dataField='isPara' label={{ text: 'Para' }} editorType='dxCheckBox' />
                        <SimpleItem dataField='isKindgerecht' label={{ text: 'Kindgerecht' }} editorType='dxCheckBox' />
                        <SimpleItem dataField='isBreak' label={{ text: 'Pause' }} editorType='dxCheckBox' />
                        <SimpleItem dataField='isCeremony' label={{ text: 'Ehrung' }} editorType='dxCheckBox' />
                        <SimpleItem dataField='noFileexport' label={{ text: 'Kein Export' }} editorType='dxCheckBox' />
                        <SimpleItem dataField='comment' label={{ text: 'Kommentar' }} editorType='dxTextArea' editorOptions={{ stylingMode: 'filled', height: 60 }} />
                    </Form>
                </div>

                <div className='dx-card' style={{ padding: 12, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 200 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, flexShrink: 0 }}>
                        Wertungen für diesen Wettkampf
                    </div>
                    <DataGrid
                        className='grid theme-dependent'
                        dataSource={eventRankings}
                        keyExpr='rankingId'
                        showBorders={false}
                        columnAutoWidth
                        rowAlternationEnabled
                        hoverStateEnabled
                        repaintChangesOnly
                        onSaving={canEdit ? handleRankingSaving : undefined}
                        loadPanel={{ enabled: isSavingRanking, text: 'Speichern...' }}
                        noDataText='Keine Wertungen für diesen Wettkampf'
                        style={{ flex: 1 }}
                    >
                        <GroupPanel visible />
                        <Grouping autoExpandAll={false} />
                        <Column dataField='rankinggroupId' caption='Wertungsgruppe' width={200}
                            lookup={{ dataSource: rankinggroupLookup, valueExpr: 'id', displayExpr: 'name' }}
                            groupIndex={0} />
                        <Column dataField='place' caption='Platz' width={80} />
                        <Column dataField='resultId' caption='Athlet/Ergebnis'
                            lookup={{ dataSource: resultLookup, valueExpr: 'id', displayExpr: 'name' }} />
                        <Column dataField='resultOk' caption='Gültig' dataType='boolean' width={80} />
                        <Column dataField='pointsTime' caption='Pkt. Zeit' width={90} />
                        <Column dataField='pointsPlace' caption='Pkt. Platz' width={90} />
                        <Editing mode='popup' allowAdding={canEdit} allowUpdating={canEdit} allowDeleting={canEdit} useIcons />
                        <Scrolling mode='virtual' />
                    </DataGrid>
                </div>
            </div>
        );
    };

    // ── Heat form ──────────────────────────────────────────────────────────
    const renderHeatForm = () => {
        if (!formData) return <div style={{ color: '#888', padding: 16 }}>Kein Lauf gefunden.</div>;
        const f = formData;
        return (
            <div className={classNames('dx-card', 'plain-styled-form', { 'view-mode': !isEditing })} style={{ padding: 16, overflow: 'auto' }}>
                {renderFormToolbar(`Lauf ${f.number || ''}`)}
                <Form formData={f} labelMode='floating' colCount={4} readOnly={!isEditing} onFieldDataChanged={(e) => updateFormField(e.dataField, e.value)}>
                    <SimpleItem dataField='eventId' label={{ text: 'Wettkampf' }} editorType='dxSelectBox' editorOptions={{ dataSource: eventLookup, valueExpr: 'id', displayExpr: 'name', stylingMode: 'filled' }} />
                    <SimpleItem dataField='number' label={{ text: 'Lauf Nr.' }} editorType='dxNumberBox' editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='estimatedStarttime' label={{ text: 'Gesch. Start' }} editorType='dxDateBox' editorOptions={{ type: 'time', displayFormat: 'HH:mm', stylingMode: 'filled' }} />
                    <SimpleItem dataField='starttime' label={{ text: 'Tats. Start' }} editorType='dxDateBox' editorOptions={{ type: 'time', displayFormat: 'HH:mm', stylingMode: 'filled' }} />
                    <SimpleItem dataField='status' label={{ text: 'Status' }} editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='statusReferee' label={{ text: 'SR-Status' }} editorOptions={{ stylingMode: 'filled' }} />
                    <SimpleItem dataField='detail' label={{ text: 'Details' }} editorType='dxTextArea' editorOptions={{ stylingMode: 'filled', height: 60 }} />
                </Form>
            </div>
        );
    };

    const renderDetail = () => {
        if (selectedType === 'session') return renderSessionForm();
        if (selectedType === 'event') return renderEventForm();
        if (selectedType === 'heat') return renderHeatForm();
        return (
            <div className='dx-card' style={{ padding: 20, color: '#888' }}>
                Bitte ein Element aus der Baumstruktur links auswählen.
            </div>
        );
    };

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <div className='competition-section'>
            <div className='section-header'>
                <div>
                    <div className='section-title'>Struktur</div>
                    <div className='section-subtitle'>Abschnitte, Wettkämpfe, Läufe und Wertungen</div>
                </div>
                <div className='section-pill subtle'>
                    {sessions?.length || 0} Abschnitt{(sessions?.length || 0) !== 1 ? 'e' : ''} ·{' '}
                    {events?.length || 0} WK ·{' '}
                    {heats?.length || 0} Läufe
                    {isReordering && <span style={{ marginLeft: 8, fontSize: 12 }}>Speichert...</span>}
                </div>
            </div>

            <SplitPanel
                storageKey='splitPanel.structure'
                defaultLeft={300}
                minLeft={160}
                minRight={280}
                left={<StructureLeftPane ref={leftPaneRef} treeItems={treeItems} expandedKeys={expandedKeys} onExpandedRowKeysChange={setExpandedKeys} onSelect={handleTreeSelect} onDragChange={handleDragChange} onReorder={handleReorder} canEdit={canEdit} />}
                right={renderDetail()}
            />
        </div>
    );
}